import { FC } from 'react';

/**
 * Types de quittance renvoyés par le back, déjà libellés.
 *
 * Le champ `receiptType` est une chaîne prête à afficher : il n'y a aucun code à
 * traduire. Cette table ne sert qu'à choisir une couleur, et retombe sur un gris neutre
 * pour toute valeur inconnue — un libellé ajouté côté back s'affichera donc tel quel
 * plutôt que de disparaître.
 */
const TONE_BY_TYPE: Record<string, string> = {
  Comptant: 'bg-green-100 text-green-800',
  Terme: 'bg-sky-100 text-sky-800',
  Sinistre: 'bg-amber-100 text-amber-800',
  Résiliation: 'bg-red-100 text-red-800',
};

const DEFAULT_TONE = 'bg-gray-100 text-gray-600';

export interface ReceiptTypeBadgeProps {
  receiptType?: string | null;
  /** Rendu compact pour les lignes de liste. */
  compact?: boolean;
  className?: string;
}

/**
 * Pastille du type de quittance : Comptant, Terme, Sinistre ou Résiliation.
 *
 * Ne rend rien quand le back ne renseigne pas le champ, plutôt qu'une pastille vide —
 * l'absence d'information ne doit pas ressembler à une information.
 */
export const ReceiptTypeBadge: FC<ReceiptTypeBadgeProps> = ({
  receiptType,
  compact,
  className = '',
}) => {
  if (!receiptType) return null;

  const tone = TONE_BY_TYPE[receiptType] ?? DEFAULT_TONE;
  const size = compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs';

  return (
    <span
      title={`Type de quittance : ${receiptType}`}
      className={`inline-flex flex-shrink-0 items-center rounded-full font-semibold ${size} ${tone} ${className}`}
    >
      {receiptType}
    </span>
  );
};
